import React, { useEffect, useRef, Suspense } from "react";
import {
  BrowserRouter as Router,
  Routes,
  Route,
  Navigate,
  useLocation,
} from "react-router-dom";
import { captureReferralFromURL } from "./shared/utils/referralUtils";
import { captureAcquisitionFromURL } from "./shared/utils/acquisitionUtils";
import { metaTrack } from "./shared/utils/metaPixel";
import { Toaster } from "./components/ui/sonner";
import { HelmetProvider } from "react-helmet-async";
import { QueryClientProvider } from "@tanstack/react-query";
import { queryClient } from "./shared/data/queryClient";
import lazyWithRetry from "./shared/utils/lazyWithRetry";

// Eagerly loaded: everything needed for the first paint of "/" (the lobby is
// the landing page for paid traffic) plus global guards/overlays. Every other
// route is code-split below so the anonymous visitor's initial bundle stays
// free of admin/affiliate/legal/checkout code.
import GameLobby from "./casino/lobby/GameLobby";
import MaintenancePage from "./pages/MaintenancePage";
import DobGate from "./components/auth/DobGate";
import GeoRestrictedGate from "./components/auth/GeoRestrictedGate";
import PhoneGate from "./components/auth/PhoneGate";

// Shared components
import ProtectedRoute from "./shared/auth/ProtectedRoute";
import PersistentCasinoLayout from "./shared/layout/PersistentCasinoLayout";
import ScrollToTop from "./shared/components/ScrollToTop";
import RouteSeo from "./shared/components/RouteSeo";
import RouteFallback from "./shared/components/RouteFallback";

// Auth Context
import { AuthProvider } from "./contexts/AuthContext";
import { BalanceProvider } from "./contexts/BalanceContext";
import { KYCProvider } from "./contexts/KYCContext";
import { NotificationProvider } from "./contexts/NotificationContext";
import { ClaimIndicatorsProvider } from "./contexts/ClaimIndicatorsContext";
import { PlayModeProvider } from "./contexts/PlayModeContext";
import SupportWidgetGate from "./shared/components/support/SupportWidgetGate";
import MaintenanceGuard from "./shared/components/guards/MaintenanceGuard";

// Casino components (lazy, main site)
const PlayerProfile = lazyWithRetry(
  () => import("./casino/profile/PlayerProfile"),
);
const OfferStore = lazyWithRetry(() => import("./casino/offers/OfferStore"));
const CheckoutPage = lazyWithRetry(() => import("./pages/CheckoutPage"));
const CardCheckoutPage = lazyWithRetry(
  () => import("./pages/checkout/CardCheckoutPage"),
);
const CryptoCheckoutPage = lazyWithRetry(
  () => import("./pages/checkout/CryptoCheckoutPage"),
);
const CheckoutSuccessPage = lazyWithRetry(
  () => import("./pages/checkout/CheckoutSuccessPage"),
);
const KycCallbackPage = lazyWithRetry(
  () => import("./pages/kyc/KycCallbackPage"),
);
const PaymentSuccessPage = lazyWithRetry(
  () => import("./pages/PaymentSuccessPage"),
);
const PaymentFailurePage = lazyWithRetry(
  () => import("./pages/PaymentFailurePage"),
);
const RedeemPage = lazyWithRetry(() => import("./casino/redeem/RedeemPage"));
const PromotionsRoute = lazyWithRetry(
  () => import("./casino/promotions/PromotionsRoute"),
);
const GameLaunch = lazyWithRetry(() => import("./casino/games/GameLaunch"));
const RedemptionTrackerPage = lazyWithRetry(
  () => import("./casino/profile/RedemptionTrackerPage"),
);

// Game category pages (lazy)
const SlotsPage = lazyWithRetry(() => import("./casino/pages/SlotsPage"));
const TableGamesPage = lazyWithRetry(
  () => import("./casino/pages/TableGamesPage"),
);
const LiveGamesPage = lazyWithRetry(
  () => import("./casino/pages/LiveGamesPage"),
);
const NewGamesPage = lazyWithRetry(() => import("./casino/pages/NewGamesPage"));
const JackpotsPage = lazyWithRetry(() => import("./casino/pages/JackpotsPage"));
const InstantGamesPage = lazyWithRetry(
  () => import("./casino/pages/InstantGamesPage"),
);
const RecentlyPlayedPage = lazyWithRetry(
  () => import("./casino/pages/RecentlyPlayedPage"),
);
const FavoritesPage = lazyWithRetry(
  () => import("./casino/pages/FavoritesPage"),
);
const VipRoute = lazyWithRetry(() => import("./casino/vip/VipRoute"));
const ReferPage = lazyWithRetry(() => import("./casino/refer/ReferPage"));
const AffiliateApplyPage = lazyWithRetry(
  () => import("./casino/affiliate/AffiliateApplyPage"),
);
const ProviderPage = lazyWithRetry(() => import("./casino/pages/ProviderPage"));
const ProvidersPage = lazyWithRetry(
  () => import("./casino/pages/ProvidersPage"),
);
const RecommendedPage = lazyWithRetry(
  () => import("./casino/pages/RecommendedPage"),
);
const PlaygroundPage = lazyWithRetry(
  () => import("./casino/pages/PlaygroundPage"),
);

// Legal pages (lazy)
const TermsPage = lazyWithRetry(() => import("./pages/legal/TermsPage"));
const PrivacyPage = lazyWithRetry(() => import("./pages/legal/PrivacyPage"));
const ResponsibleGamingPage = lazyWithRetry(
  () => import("./pages/legal/ResponsibleGamingPage"),
);
const AMOEPage = lazyWithRetry(() => import("./pages/legal/AMOEPage"));

// Blog (lazy, public Guides)
const BlogListPage = lazyWithRetry(() => import("./pages/blog/BlogListPage"));
const BlogPostPage = lazyWithRetry(() => import("./pages/blog/BlogPostPage"));

// Paid-traffic landing page (lazy)
const FreeOfferPage = lazyWithRetry(
  () => import("./pages/landing/FreeOfferPage"),
);

const NotFoundPage = lazyWithRetry(() => import("./pages/NotFoundPage"));

// Auth components (lazy)
const AdminLogin = lazyWithRetry(() => import("./shared/auth/AdminLogin"));
const SignupPage = lazyWithRetry(() => import("./pages/auth/SignupPage"));
const SignInPage = lazyWithRetry(() => import("./pages/auth/SignInPage"));
const ForgotPasswordPage = lazyWithRetry(
  () => import("./pages/auth/ForgotPasswordPage"),
);
const ResetPasswordPage = lazyWithRetry(
  () => import("./pages/auth/ResetPasswordPage"),
);
const AuthCallback = lazyWithRetry(() => import("./pages/auth/AuthCallback"));

// Admin panel (lazy, staff only, must never weigh down the player bundle)
const AdminLayout = lazyWithRetry(() => import("./admin/AdminLayout"));

// Affiliate dashboard (lazy)
const AffiliateLayout = lazyWithRetry(
  () => import("./affiliate/AffiliateLayout"),
);

// KYC Components (lazy)
const KYCVerificationPage = lazyWithRetry(
  () => import("./pages/KYCVerificationPage"),
);

/**
 * Fires a Meta Pixel PageView on every SPA route change. The base pixel
 * snippet in index.html already fires the initial PageView on hard load, so
 * we skip the very first render to avoid double-counting it.
 */
function PixelPageView() {
  const { pathname } = useLocation();
  const isFirstRender = useRef(true);

  useEffect(() => {
    if (isFirstRender.current) {
      isFirstRender.current = false;
      return;
    }
    metaTrack("PageView");
  }, [pathname]);

  return null;
}

function App() {
  // Capture referral code + acquisition source (UTM / referrer) from URL on
  // first load.
  useEffect(() => {
    captureReferralFromURL();
    captureAcquisitionFromURL();
  }, []);

  return (
    <QueryClientProvider client={queryClient}>
      <HelmetProvider>
        <AuthProvider>
          <BalanceProvider>
            <PlayModeProvider>
              <KYCProvider>
                <NotificationProvider>
                  <MaintenanceGuard />

                  <ClaimIndicatorsProvider>
                    <Router>
                      {/* Hard guard: if maintenance is enabled on server, we’ll redirect via layout */}

                      <ScrollToTop />
                      <PixelPageView />
                      <RouteSeo />
                      {/* Reads the support_widget_enabled flag once, then
                          mounts the support chat. Off renders nothing. */}
                      <SupportWidgetGate />
                      {/* Blocking DOB sheet for social-login users with no DOB
                          on file (otherwise locked out by the age/geo gate). */}
                      <DobGate />
                      {/* Blocking geo-restricted sheet: shown only when login
                          geo re-verification returns an explicit geo_blocked
                          verdict (live restricted location). Fails open on
                          error/429; admins never trigger it. */}
                      <GeoRestrictedGate />
                      {/* Contextual Didit phone-verification sheet: opens on
                          403 PHONE_REQUIRED (free-CB claims / redemptions),
                          never walls entry, play, or purchase. */}
                      <PhoneGate />
                      <div className="min-h-screen bg-background text-white">
                        {/* Fallback for lazy routes rendered outside
                            PersistentCasinoLayout (admin, affiliate, auth,
                            legal). Routes inside the layout suspend on the
                            boundary around its <Outlet/> instead, keeping
                            the sidebar/header visible. React Router v7
                            wraps navigations in startTransition, so this
                            only shows on hard loads. In-app navigation
                            keeps the previous screen until the chunk is
                            ready. */}
                        <Suspense fallback={<RouteFallback fullScreen />}>
                          <Routes>
                            {/* Maintenance route (standalone, no layout) */}
                            <Route
                              path="/maintenance"
                              element={<MaintenancePage />}
                            />

                            {/* Auth routes */}
                            <Route
                              path="/login"
                              element={<Navigate to="/signin" replace />}
                            />
                            <Route path="/signup" element={<SignupPage />} />
                            <Route path="/signin" element={<SignInPage />} />
                            <Route
                              path="/forgot-password"
                              element={<ForgotPasswordPage />}
                            />
                            <Route
                              path="/reset-password"
                              element={<ResetPasswordPage />}
                            />
                            <Route
                              path="/auth/callback"
                              element={<AuthCallback />}
                            />
                            <Route
                              path="/admin/login"
                              element={<AdminLogin />}
                            />

                            {/* Didit verification return (phone gate + KYC) */}
                            <Route
                              path="/kyc/callback"
                              element={<KycCallbackPage />}
                            />

                            {/* Payment result pages */}
                            <Route
                              path="/payment/success"
                              element={<PaymentSuccessPage />}
                            />
                            <Route
                              path="/payment/failure"
                              element={<PaymentFailurePage />}
                            />

                            {/* KYC Verification Page */}
                            <Route
                              path="/verify"
                              element={
                                <ProtectedRoute role="player">
                                  <KYCVerificationPage />
                                </ProtectedRoute>
                              }
                            />

                            {/* Casino routes with persistent layout */}
                            <Route
                              path="/"
                              element={<PersistentCasinoLayout />}
                            >
                              <Route index element={<GameLobby />} />
                              <Route
                                path="profile"
                                element={
                                  <ProtectedRoute role="player">
                                    <PlayerProfile />
                                  </ProtectedRoute>
                                }
                              />
                              <Route
                                path="profile/:tab"
                                element={
                                  <ProtectedRoute role="player">
                                    <PlayerProfile />
                                  </ProtectedRoute>
                                }
                              />

                              <Route
                                path="profile/transactions/:transactionId"
                                element={
                                  <ProtectedRoute role="player">
                                    <RedemptionTrackerPage />
                                  </ProtectedRoute>
                                }
                              />

                              <Route
                                path="offers"
                                element={
                                  <ProtectedRoute role="player">
                                    <OfferStore />
                                  </ProtectedRoute>
                                }
                              />
                              <Route
                                path="checkout/:offerId"
                                element={
                                  <ProtectedRoute role="player">
                                    <CheckoutPage />
                                  </ProtectedRoute>
                                }
                              />
                              <Route
                                path="checkout/card"
                                element={
                                  <ProtectedRoute role="player">
                                    <CardCheckoutPage />
                                  </ProtectedRoute>
                                }
                              />
                              <Route
                                path="checkout/crypto"
                                element={
                                  <ProtectedRoute role="player">
                                    <CryptoCheckoutPage />
                                  </ProtectedRoute>
                                }
                              />
                              <Route
                                path="checkout/success"
                                element={
                                  <ProtectedRoute role="player">
                                    <CheckoutSuccessPage />
                                  </ProtectedRoute>
                                }
                              />
                              <Route
                                path="redeem"
                                element={
                                  <ProtectedRoute role="player">
                                    <RedeemPage />
                                  </ProtectedRoute>
                                }
                              />
                              <Route
                                path="promotions"
                                element={<PromotionsRoute />}
                              />
                              <Route path="vip" element={<VipRoute />} />
                              <Route
                                path="refer"
                                element={
                                  <ProtectedRoute role="player">
                                    <ReferPage />
                                  </ProtectedRoute>
                                }
                              />
                              <Route
                                path="affiliate/apply"
                                element={
                                  <ProtectedRoute role="player">
                                    <AffiliateApplyPage />
                                  </ProtectedRoute>
                                }
                              />

                              {/* Hidden test routes */}
                              <Route
                                path="recommended"
                                element={<RecommendedPage />}
                              />
                              <Route
                                path="playground"
                                element={<PlaygroundPage />}
                              />

                              {/* Game category pages */}

                              <Route
                                path="games/slots"
                                element={<SlotsPage />}
                              />
                              <Route
                                path="games/table"
                                element={<TableGamesPage />}
                              />
                              <Route
                                path="games/live"
                                element={<LiveGamesPage />}
                              />
                              <Route
                                path="games/instant"
                                element={<InstantGamesPage />}
                              />
                              <Route
                                path="games/new"
                                element={<NewGamesPage />}
                              />
                              <Route
                                path="games/jackpots"
                                element={<JackpotsPage />}
                              />
                              <Route
                                path="games/recent"
                                element={<RecentlyPlayedPage />}
                              />
                              <Route
                                path="games/favorites"
                                element={<FavoritesPage />}
                              />
                              <Route
                                path="providers"
                                element={<ProvidersPage />}
                              />
                              <Route
                                path="games/provider/:providerSlug"
                                element={<ProviderPage />}
                              />
                              <Route
                                path="games/:gameId"
                                element={
                                  <ProtectedRoute role="player">
                                    <GameLaunch />
                                  </ProtectedRoute>
                                }
                              />
                              <Route
                                path="games/:gameId/fun"
                                element={
                                  <ProtectedRoute role="player">
                                    <GameLaunch />
                                  </ProtectedRoute>
                                }
                              />

                              {/* 404 Catch-all route - Now uses persistent layout */}
                              <Route path="*" element={<NotFoundPage />} />
                            </Route>

                            {/* Legal pages */}
                            <Route path="/terms" element={<TermsPage />} />
                            <Route path="/privacy" element={<PrivacyPage />} />
                            <Route
                              path="/responsible-gaming"
                              element={<ResponsibleGamingPage />}
                            />
                            <Route path="/amoe" element={<AMOEPage />} />

                            {/* Blog / Guides (public, reachable logged-out) */}
                            <Route path="/blog" element={<BlogListPage />} />
                            <Route
                              path="/blog/:slug"
                              element={<BlogPostPage />}
                            />

                            {/* Paid-traffic landing page (public, noindex) */}
                            <Route path="/free" element={<FreeOfferPage />} />

                            {/* Admin routes (staff only) */}
                            <Route
                              path="/admin/*"
                              element={
                                <ProtectedRoute role="staff">
                                  <AdminLayout />
                                </ProtectedRoute>
                              }
                            />

                            {/* Affiliate routes */}
                            <Route
                              path="/affiliate/*"
                              element={<AffiliateLayout />}
                            />
                          </Routes>
                        </Suspense>
                      </div>

                      {/* Global Toast Notifications (sonner, themed via brand tokens) */}
                      <Toaster />
                    </Router>
                  </ClaimIndicatorsProvider>
                </NotificationProvider>
              </KYCProvider>
            </PlayModeProvider>
          </BalanceProvider>
        </AuthProvider>
      </HelmetProvider>
    </QueryClientProvider>
  );
}

export default App;
